import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  PresenceBadges,
  type SelfColorConfigurationAnchor,
} from "../../../components/presence-badges";
import { useCurrentActorState } from "../../../auth/use-current-actor";
import {
  VIEWER_COLOR_PREFERENCE_STORAGE_KEY,
  VIEWER_PRESET_COLORS,
  addViewerCustomColor,
  loadViewerCustomColors,
  removeViewerCustomColor,
  updateViewerCustomColor,
} from "../../../lib/viewer-color-preference";
import { normalizeHexColor } from "../../../lib/hex-color";
import { useCanvasViewersStore } from "../store/canvas-viewers-store";
import { useCtrlWheelZoom } from "../input/use-ctrl-wheel-zoom";
import { useShowToast } from "../notice/use-show-toast";
import { ColorSwatchList, SubPanel } from "../toolbar/toolbar-controls";
import { ToolbarDismissBoundaryContext } from "../toolbar/toolbar-parent-child-panel";
import {
  resolveSelectionPanelSafeArea,
  type PanelRect,
  type PanelSize,
} from "../toolbar/selection-panel-placement";

const MY_COLOR_PANEL_GAP_PX = 8;
const MY_COLOR_PANEL_DEFAULT_SIZE = { width: 360, height: 84 } as const;

interface MyColorPanelPlacement {
  readonly left: number;
  readonly top: number;
  readonly maxWidth: number;
  readonly maxHeight: number;
}

/** 右上の自己アバターでは下側を優先し、収まらない場合だけ上側へ反転する。 */
export function resolveMyColorPanelPlacement(input: {
  readonly anchor: PanelRect;
  readonly panel: PanelSize;
  readonly viewport: PanelSize;
}): MyColorPanelPlacement {
  const safeArea = resolveSelectionPanelSafeArea(input.viewport);
  const safeRight = safeArea.left + safeArea.width;
  const safeBottom = safeArea.top + safeArea.height;
  const anchorBottom = input.anchor.top + input.anchor.height;
  const availableBottom = safeBottom - anchorBottom;
  const availableTop = input.anchor.top - safeArea.top;
  const placeBelow =
    availableBottom >= input.panel.height + MY_COLOR_PANEL_GAP_PX ||
    availableBottom >= availableTop;
  const maxLeft = Math.max(safeArea.left, safeRight - input.panel.width);
  const maxTop = Math.max(safeArea.top, safeBottom - input.panel.height);
  const left = clamp(
    input.anchor.left + input.anchor.width - input.panel.width,
    safeArea.left,
    maxLeft
  );
  const requestedTop = placeBelow
    ? anchorBottom + MY_COLOR_PANEL_GAP_PX
    : input.anchor.top - MY_COLOR_PANEL_GAP_PX - input.panel.height;
  return {
    left,
    top: clamp(requestedTop, safeArea.top, maxTop),
    maxWidth: safeArea.width,
    maxHeight: safeArea.height,
  };
}

// 右上：参加ユーザー一覧。
// 他参加者はフォーカス、自分はマイカラー設定を開く。
export function PresenceList(): JSX.Element {
  const viewers = useCanvasViewersStore((state) => state.viewers);
  const focusedUserId = useCanvasViewersStore((state) => state.focusedUserId);
  const setFocusedUserId = useCanvasViewersStore((state) => state.setFocusedUserId);
  const currentActorState = useCurrentActorState();
  const currentActor = currentActorState.actor;
  const showToast = useShowToast();
  const snapshotViewers = Object.values(viewers);
  // snapshot 到着前・自己除外構成でも、設定入口となる自己アバターだけは表示する。
  const list =
    currentActor.userId.length > 0 &&
    !snapshotViewers.some((viewer) => viewer.userId === currentActor.userId)
      ? [currentActor, ...snapshotViewers]
      : snapshotViewers;
  const rootRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const panelId = useId();
  const [portalRoots] = useState(() => new Set<HTMLElement>());
  const [colorAnchor, setColorAnchor] = useState<SelfColorConfigurationAnchor | null>(null);
  const [placement, setPlacement] = useState<MyColorPanelPlacement | null>(null);
  const [customColors, setCustomColors] = useState<readonly string[]>(loadViewerCustomColors);
  useCtrlWheelZoom(rootRef);

  const closeColorPanel = useCallback((restoreFocus: boolean): void => {
    setColorAnchor((current) => {
      if (restoreFocus && current?.restoreFocusElement.isConnected) {
        window.requestAnimationFrame(() => current.restoreFocusElement.focus());
      }
      return null;
    });
    setPlacement(null);
  }, []);

  const updatePlacement = useCallback((): void => {
    if (!colorAnchor) return;
    const anchorElement = colorAnchor.anchorElement;
    if (!anchorElement.isConnected) {
      closeColorPanel(false);
      return;
    }
    const rect = anchorElement.getBoundingClientRect();
    if (!isVisibleRect(rect, { width: window.innerWidth, height: window.innerHeight })) {
      closeColorPanel(false);
      return;
    }
    const panelRect = panelRef.current?.getBoundingClientRect();
    const panel = {
      width: panelRect && panelRect.width > 0 ? panelRect.width : MY_COLOR_PANEL_DEFAULT_SIZE.width,
      height:
        panelRect && panelRect.height > 0 ? panelRect.height : MY_COLOR_PANEL_DEFAULT_SIZE.height,
    };
    const next = resolveMyColorPanelPlacement({
      anchor: { left: rect.left, top: rect.top, width: rect.width, height: rect.height },
      panel,
      viewport: { width: window.innerWidth, height: window.innerHeight },
    });
    setPlacement((previous) =>
      previous &&
      previous.left === next.left &&
      previous.top === next.top &&
      previous.maxWidth === next.maxWidth &&
      previous.maxHeight === next.maxHeight
        ? previous
        : next
    );
  }, [closeColorPanel, colorAnchor]);

  useLayoutEffect(updatePlacement, [customColors, updatePlacement]);

  useEffect(() => {
    if (!colorAnchor) return;
    panelRef.current?.querySelector<HTMLButtonElement>('[role="radio"]')?.focus();
  }, [colorAnchor]);

  useEffect(() => {
    if (!colorAnchor) return undefined;
    const onViewportChange = (): void => updatePlacement();
    window.addEventListener("resize", onViewportChange);
    window.addEventListener("scroll", onViewportChange, true);
    return () => {
      window.removeEventListener("resize", onViewportChange);
      window.removeEventListener("scroll", onViewportChange, true);
    };
  }, [colorAnchor, updatePlacement]);

  useEffect(() => {
    const panel = panelRef.current;
    if (!colorAnchor || !panel || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(updatePlacement);
    observer.observe(panel);
    return () => observer.disconnect();
  }, [colorAnchor, updatePlacement]);

  useEffect(() => {
    if (!colorAnchor) return undefined;
    const dismiss = (event: PointerEvent): void => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (panelRef.current?.contains(target) || colorAnchor.anchorElement.contains(target)) return;
      // 色エディタや右クリックメニューが開いている間は、先にそのオーバーレイだけを閉じる。
      if (portalRoots.size > 0) return;
      closeColorPanel(false);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== "Escape" || event.isComposing || portalRoots.size > 0) return;
      event.preventDefault();
      closeColorPanel(true);
    };
    window.addEventListener("pointerdown", dismiss, true);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("pointerdown", dismiss, true);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [closeColorPanel, colorAnchor, portalRoots]);

  useEffect(() => {
    const onStorage = (event: StorageEvent): void => {
      if (event.key === null || event.key === VIEWER_COLOR_PREFERENCE_STORAGE_KEY) {
        setCustomColors(loadViewerCustomColors());
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const onFocusViewer = (userId: string): void => {
    if (userId === currentActor.userId) return;
    setFocusedUserId(focusedUserId === userId ? null : userId);
  };

  const onConfigureSelfColor = (anchor: SelfColorConfigurationAnchor): void => {
    if (!currentActorState.colorEditable || currentActor.userId.length === 0) return;
    if (colorAnchor?.anchorElement === anchor.anchorElement) {
      closeColorPanel(true);
      return;
    }
    setColorAnchor(anchor);
  };

  const onChooseColor = (color: string): void => {
    if (!currentActorState.updateColor(color)) {
      showToast("warning", "マイカラーを保存できませんでした。直前の色へ戻しました。");
    }
  };

  const onAddCustomColor = (color: string): void => {
    const result = addViewerCustomColor(color);
    setCustomColors(result.colors);
    if (!result.saved) showToast("warning", "カスタム色を保存できませんでした。");
  };

  const onUpdateCustomColor = (previous: string, next: string): void => {
    const result = updateViewerCustomColor(previous, next);
    setCustomColors(result.colors);
    if (!result.saved) {
      showToast("warning", "カスタム色を保存できませんでした。");
      return;
    }
    if (result.applied && normalizeHexColor(currentActor.color) === normalizeHexColor(previous)) {
      onChooseColor(next);
    }
  };

  const onDeleteCustomColor = (color: string): void => {
    const result = removeViewerCustomColor(color);
    setCustomColors(result.colors);
    if (!result.saved) showToast("warning", "カスタム色を保存できませんでした。");
  };

  const panel = colorAnchor
    ? createPortal(
        <ToolbarDismissBoundaryContext.Provider value={portalRoots}>
          <div
            ref={panelRef}
            id={panelId}
            role="dialog"
            aria-label="マイカラー"
            className="canvas-my-color-panel"
            data-testid="my-color-panel"
            style={{
              left: `${placement?.left ?? 0}px`,
              top: `${placement?.top ?? 0}px`,
              maxWidth: `${placement?.maxWidth ?? window.innerWidth}px`,
              maxHeight: `${placement?.maxHeight ?? window.innerHeight}px`,
              visibility: placement ? "visible" : "hidden",
            }}
          >
            <SubPanel label="マイカラー">
              <ColorSwatchList
                ariaLabel="マイカラー"
                presetColors={VIEWER_PRESET_COLORS}
                customColors={customColors}
                value={currentActor.color}
                onChoose={onChooseColor}
                onAddColor={onAddCustomColor}
                onUpdateCustomColor={onUpdateCustomColor}
                onDeleteCustomColor={onDeleteCustomColor}
              />
            </SubPanel>
          </div>
        </ToolbarDismissBoundaryContext.Provider>,
        document.body
      )
    : null;

  return (
    <div ref={rootRef} data-focus-control>
      <PresenceBadges
        viewers={list}
        onFocusViewer={onFocusViewer}
        focusedUserId={focusedUserId}
        currentUserId={currentActor.userId}
        currentUserColor={currentActor.color}
        onConfigureSelfColor={currentActorState.colorEditable ? onConfigureSelfColor : undefined}
        selfColorPanelOpen={colorAnchor !== null}
        selfColorPanelId={panelId}
      />
      {panel}
    </div>
  );
}

function clamp(value: number, lower: number, upper: number): number {
  return Math.min(Math.max(value, lower), upper);
}

function isVisibleRect(rect: DOMRect, viewport: PanelSize): boolean {
  return (
    rect.width > 0 &&
    rect.height > 0 &&
    rect.right > 0 &&
    rect.bottom > 0 &&
    rect.left < viewport.width &&
    rect.top < viewport.height
  );
}
